import { useMemo } from 'react';
import { LocationFeatureResponse } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { FeatureFlagStrategy } from '../types';
import { useAppFlagStore } from './use-feature-flag';

const DEFAULT_ACTIVE_STATES = [State.ACTIVE];

/**
 * Resolves multiple aliased customization flag names for the same feature,
 * merging their per-location results into a single map.
 *
 * This is a convenience wrapper over `getCustomizationFlagLocationMap` that
 * iterates through each flag name in order, collects per-location data, and
 * lets latter flag names override earlier ones for the same location.
 *
 * @param flagNames - One or more flag names for the same feature. Order matters — latter flags override earlier ones per location.
 * @param locationIds - Location IDs to evaluate the flags against.
 * @param activeStates - Which {@link State} values count as "active". Defaults to `[State.ACTIVE]`.
 * @param strategy - `'ANY'` (default) = any location has flag active; `'ALL'` = every location has flag active.
 *
 * @returns
 * - `isActive` — boolean derived from `locationFlagValueMap` using the given `strategy`.
 * - `locationFlagMap` — `Record<locationId, LocationFeatureResponse>` of merged flag data.
 * - `locationFlagValueMap` — `Record<locationId, boolean>` indicating whether flag in each location is active.
 */
export const useAliasedCustomizationFlag = (
  flagNames: readonly string[],
  locationIds: string[],
  activeStates: State[] = DEFAULT_ACTIVE_STATES,
  strategy: FeatureFlagStrategy = 'ANY'
) => {
  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  return useMemo(() => {
    // locationId -> flagData map. The latter flags in the flagNames array will override the earlier ones if there are duplicate locationIds
    const locationFlagMap = flagNames.reduce<Record<string, LocationFeatureResponse>>((acc, flagName) => {
      const flagLocationMap = getCustomizationFlagLocationMap({ locationIds, flagName });
      const filteredLocationMap = Object.entries(flagLocationMap).reduce<Record<string, LocationFeatureResponse>>(
        (filteredAcc, [locationId, flagData]) => {
          // Only include locations with flag data
          if (flagData) {
            filteredAcc[locationId] = flagData;
          }
          return filteredAcc;
        },
        {}
      );
      return { ...acc, ...filteredLocationMap };
    }, {});

    const locationFlagValueMap = Object.entries(locationFlagMap).reduce<Record<string, boolean>>(
      (acc, [locationId, flagData]) => {
        acc[locationId] = activeStates.includes(flagData?.state ?? State.HIDE);
        return acc;
      },
      {}
    );

    const flagValues = Object.values(locationFlagValueMap);
    const isActive =
      strategy === 'ALL'
        ? flagValues.length > 0 && flagValues.every((isFlagActive) => isFlagActive)
        : flagValues.some((isFlagActive) => isFlagActive);

    return { isActive, locationFlagMap, locationFlagValueMap };
  }, [activeStates, flagNames, getCustomizationFlagLocationMap, locationIds, strategy]);
};
